import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  FormGroup,
  Input,
  SegmentedControl,
  Select,
  TableShell,
} from '../../../components/ui'
import Icon from '../../../components/Icon'
import {
  buildActualAnalytics,
  buildPlannedAnalytics,
  getDepartmentIncomeHeroNote,
  hasVisibleMoneyAmount,
  pluralizeRu,
} from './departmentDetailUtils'

function EmptyState ({ children, hint }) {
  return (
    <div className="department-income-empty">
      <Icon name="info" size={24} className="empty-icon" />
      <p className="info-note">{children}</p>
      {hint && <p className="info-note-hint">{hint}</p>}
    </div>
  )
}

function formatPercent (value) {
  const n = Number(value) || 0
  const rounded = Math.round(n * 10) / 10
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : String(rounded).replace('.', ',')}%`
}

function formatRolePercent (value) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return `${n.toFixed(3).replace(/\.?0+$/, '').replace('.', ',')}%`
}

function normalizeStatusText (status) {
  return String(status || '').trim().toLowerCase()
}

function isStageNew (status) {
  const value = normalizeStatusText(status)
  return value.includes('нов') || value === 'не в работе'
}

function isStageWorkDone (status) {
  const value = normalizeStatusText(status)
  return (
    value.includes('работы заверш') ||
    value.includes('завершен') ||
    value.includes('завершён') ||
    value.includes('закры') ||
    value.includes('заакт') ||
    value.includes('сдан')
  )
}

function getRemainingStatusBucket (status) {
  if (isStageNew(status)) return 'new'
  if (isStageWorkDone(status)) return 'doneWork'
  return 'active'
}

function getIncomeStatusTone (status) {
  const value = normalizeStatusText(status)
  if (value.includes('закры') || value.includes('заакт') || isStageWorkDone(value)) return 'ds-status-pill-done'
  if (isStageNew(value)) return 'ds-status-pill-draft'
  return 'ds-status-pill-active'
}

function getObjectContractLabel (row) {
  const objectName = row?.objectName || row?.projectName || 'Без объекта'
  const contractNumber = row?.contractNumber || ''
  return contractNumber ? `${objectName} | ${contractNumber}` : objectName
}

function AmountTable ({ title, meta, columns, rows, total, formatMoney, emptyText, footerCells }) {
  return (
    <section className="department-detail-section" aria-labelledby={`${title}-heading`}>
      <div className="department-detail-section-head">
        <h2 id={`${title}-heading`}>{title}</h2>
        {meta && <span>{meta}</span>}
      </div>
      <TableShell className="department-detail-table department-detail-table-scroll">
        <table className="ds-data-table ds-income-table department-detail-data-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className={column.align === 'right' ? 'ds-align-right' : ''}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((row) => (
              <tr key={row.id || row.name || row.stageName}>
                {columns.map((column) => (
                  <td key={column.key} className={column.align === 'right' ? 'ds-mono' : ''}>
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            )) : (
              <tr>
                <td colSpan={columns.length} className="department-detail-empty-cell">{emptyText}</td>
              </tr>
            )}
          </tbody>
          {footerCells ? (
            <tfoot>
              <tr>
                {footerCells.map((cell, index) => (
                  <td
                    key={`${cell.label || cell.value || index}`}
                    colSpan={cell.colSpan}
                    className={cell.className || ''}
                  >
                    {cell.value}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : total != null && (
            <tfoot>
              <tr>
                <td colSpan={columns.length - 1}>Итого</td>
                <td className="ds-mono">{formatMoney(total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </TableShell>
    </section>
  )
}

function ProjectIncomeTable ({ projects, total, formatMoney }) {
  const navigate = useNavigate()
  const stagesCount = projects.reduce((sum, project) => sum + (project.stages?.length || 0), 0)

  return (
    <section className="department-detail-section" aria-labelledby="department-project-income-heading">
      <div className="department-detail-section-head">
        <h2 id="department-project-income-heading">По проектам</h2>
        <span>
          {projects.length} {pluralizeRu(projects.length, 'проект', 'проекта', 'проектов')}
          {' · '}
          {stagesCount} {pluralizeRu(stagesCount, 'стадия', 'стадии', 'стадий')}
        </span>
      </div>
      <TableShell className="department-detail-table department-detail-table-scroll">
        <table className="ds-data-table ds-income-table department-detail-data-table department-project-income-table">
          <thead>
            <tr>
              <th>Проект / Стадия</th>
              <th className="ds-align-right">Сумма отдела</th>
            </tr>
          </thead>
          <tbody>
            {projects.length ? projects.map((project) => (
              <React.Fragment key={project.objectId || project.name}>
                <tr className="department-income-project-row">
                  <td>
                    <span className="department-income-entity">
                      <span className="department-income-dot department-income-dot-project" aria-hidden />
                      {project.objectId ? (
                        <button
                          type="button"
                          className="department-income-link"
                          onClick={() => navigate(`/object/${project.objectId}`)}
                        >
                          {project.name}
                        </button>
                      ) : (
                        <strong>{project.name}</strong>
                      )}
                    </span>
                  </td>
                  <td className="ds-mono">{formatMoney(project.total)}</td>
                </tr>
                {(project.stages || []).map((stage) => (
                  <tr key={stage.id || stage.stageName} className="department-income-stage-row">
                    <td>
                      <span className="department-income-stage-cell">
                        {stage.id ? (
                          <button
                            type="button"
                            className="department-income-link department-income-link-stage"
                            onClick={() => navigate(`/stage/${encodeURIComponent(stage.public_id || stage.id)}`)}
                          >
                            {stage.stageName}
                          </button>
                        ) : (
                          <strong>{stage.stageName}</strong>
                        )}
                        {stage.stageBudget ? (
                          <small>{formatPercent((stage.total / stage.stageBudget) * 100)} от бюджета {formatMoney(stage.stageBudget)}</small>
                        ) : null}
                      </span>
                    </td>
                    <td className="ds-mono">{formatMoney(stage.total)}</td>
                  </tr>
                ))}
              </React.Fragment>
            )) : (
              <tr>
                <td colSpan={2} className="department-detail-empty-cell">Нет распределения по проектам</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td>Итого</td>
              <td className="ds-mono">{formatMoney(total)}</td>
            </tr>
          </tfoot>
        </table>
      </TableShell>
    </section>
  )
}

function MatrixIncomeTable ({ rows, total, formatMoney }) {
  return (
    <section className="department-detail-section" aria-labelledby="department-earned-matrix-heading">
      <div className="department-detail-section-head">
        <h2 id="department-earned-matrix-heading">Кто за что заработал</h2>
        <span>матрица сотрудник × стадия</span>
      </div>
      <TableShell className="department-detail-table department-detail-table-scroll">
        <table className="ds-data-table ds-income-table department-detail-data-table department-income-matrix-table">
          <colgroup>
            <col className="department-income-matrix-col-name" />
            <col className="department-income-matrix-col-amount" />
            <col className="department-income-matrix-col-percent" />
          </colgroup>
          <thead>
            <tr>
              <th>Стадия / Сотрудник</th>
              <th className="ds-align-right">Сумма</th>
              <th className="ds-align-right">Процент сотрудника</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((row, index) => (
              <React.Fragment key={row.id || row.stageName}>
                <tr className="department-income-matrix-stage-row">
                  <td>
                    <span className="department-income-stage-cell department-income-matrix-stage-cell">
                      <span className="department-income-matrix-objectline">
                        <span
                          className={`department-income-dot department-income-dot-object department-income-dot-palette-${(index % 7) + 1}`}
                          aria-hidden
                        />
                        <strong title={getObjectContractLabel(row)}>{getObjectContractLabel(row)}</strong>
                      </span>
                      <small>{row.stageType || row.stageName}</small>
                    </span>
                  </td>
                  <td className="ds-mono">{formatMoney(row.total)}</td>
                  <td />
                </tr>
                {(row.employees || []).map((employee) => (
                  <tr key={`${row.id}-${employee.name}-${employee.subTeam || ''}`} className="department-income-subrow">
                    <td>
                      <span className="department-income-employee-cell">
                        <span className="department-income-employee-arrow" aria-hidden>↳</span>
                        <span className="department-income-employee-label">
                          <span>{employee.name}</span>
                          {(employee.subTeam || employee.rolePercent !== null) && (
                            <small>{[employee.subTeam, formatRolePercent(employee.rolePercent)].filter(Boolean).join(' · ')}</small>
                          )}
                        </span>
                      </span>
                    </td>
                    <td className="ds-mono">{formatMoney(employee.amount)}</td>
                    <td className="ds-mono">{formatPercent(employee.percent)}</td>
                  </tr>
                ))}
              </React.Fragment>
            )) : (
              <tr>
                <td colSpan={3} className="department-detail-empty-cell">Нет фактического распределения по актам</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td>Итого</td>
              <td className="ds-mono">{formatMoney(total)}</td>
              <td className="ds-mono">100%</td>
            </tr>
          </tfoot>
        </table>
      </TableShell>
    </section>
  )
}

function RemainingIncomeTable ({ stages, formatMoney }) {
  const [filter, setFilter] = useState('all')
  const visibleStages = useMemo(() => (
    stages.filter((stage) => hasVisibleMoneyAmount(stage?.total))
  ), [stages])
  const counts = useMemo(() => ({
    all: visibleStages.length,
    active: visibleStages.filter((stage) => getRemainingStatusBucket(stage.status) === 'active').length,
    doneWork: visibleStages.filter((stage) => getRemainingStatusBucket(stage.status) === 'doneWork').length,
    new: visibleStages.filter((stage) => getRemainingStatusBucket(stage.status) === 'new').length,
  }), [visibleStages])
  const filteredStages = useMemo(() => {
    if (filter === 'active') return visibleStages.filter((stage) => getRemainingStatusBucket(stage.status) === 'active')
    if (filter === 'doneWork') return visibleStages.filter((stage) => getRemainingStatusBucket(stage.status) === 'doneWork')
    if (filter === 'new') return visibleStages.filter((stage) => getRemainingStatusBucket(stage.status) === 'new')
    return visibleStages
  }, [filter, visibleStages])
  const filteredTotal = filteredStages.reduce((sum, stage) => sum + (Number(stage.total) || 0), 0)

  const chips = [
    { id: 'all', label: 'Все', count: counts.all },
    { id: 'active', label: 'В работе', count: counts.active },
    { id: 'doneWork', label: 'Работы завершены', count: counts.doneWork },
    { id: 'new', label: 'Новые', count: counts.new },
  ]
  const filterLabels = {
    all: 'все статусы',
    active: 'в работе',
    doneWork: 'работы завершены',
    new: 'новые',
  }

  return (
    <section className="department-detail-section department-detail-section-soft" aria-labelledby="department-remaining-income-heading">
      <div className="department-detail-section-head">
        <h2 id="department-remaining-income-heading">Что ещё предстоит заработать</h2>
        <span>
          {filteredStages.length} {pluralizeRu(filteredStages.length, 'стадия', 'стадии', 'стадий')}
          {' · '}
          {filterLabels[filter]}
        </span>
      </div>
      <div className="department-remain-filter" role="tablist" aria-label="Фильтр оставшихся стадий">
        {chips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            role="tab"
            aria-selected={filter === chip.id}
            className={`department-remain-chip${filter === chip.id ? ' department-remain-chip-active' : ''}`}
            onClick={() => setFilter(chip.id)}
          >
            {chip.label} <span>{chip.count}</span>
          </button>
        ))}
      </div>
      <TableShell className="department-detail-table department-detail-table-scroll">
        <table className="ds-data-table ds-income-table department-detail-data-table department-remaining-income-table">
          <colgroup>
            <col className="department-remaining-col-name" />
            <col className="department-remaining-col-status" />
            <col className="department-remaining-col-date" />
            <col className="department-remaining-col-amount" />
          </colgroup>
          <thead>
            <tr>
              <th>Проект / Стадия</th>
              <th>Статус</th>
              <th>Срок сдачи</th>
              <th className="ds-align-right">Осталось получить</th>
            </tr>
          </thead>
          <tbody>
            {filteredStages.length ? filteredStages.map((stage) => (
              <tr key={stage.stageId || stage.stageName}>
                <td>
                  <span className="department-income-stage-cell">
                    <strong>{getObjectContractLabel(stage)}</strong>
                    <small>{stage.stageType || stage.stageName}</small>
                  </span>
                </td>
                <td>
                  <span className={`ds-status-pill ${getIncomeStatusTone(stage.status)}`}>
                    {stage.status || 'В работе'}
                  </span>
                </td>
                <td>{stage.plannedDate ? new Date(stage.plannedDate).toLocaleDateString('ru-RU') : '—'}</td>
                <td className="ds-mono">{formatMoney(stage.total)}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={4} className="department-detail-empty-cell">В этом срезе стадий нет</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>Всего</td>
              <td className="ds-mono">{formatMoney(filteredTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </TableShell>
    </section>
  )
}

function DepartmentIncomeDashboard ({
  activeTeam,
  plannedTeam,
  actualTeam,
  actualTeams,
  plannedLoading,
  plannedError,
  actualLoading,
  actualError,
  actualTotal,
  incomeYear,
  setIncomeYear,
  incomeQuarter,
  setIncomeQuarter,
  stageSearchInput,
  setStageSearchInput,
  sortOrder,
  setSortOrder,
  handleExportCSV,
  formatMoney,
}) {
  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear()
    return [current - 2, current - 1, current].map((year) => ({ value: year, label: String(year) }))
  }, [])
  const quarterOptions = [
    { value: '1', label: 'I кв' },
    { value: '2', label: 'II кв' },
    { value: '3', label: 'III кв' },
    { value: '4', label: 'IV кв' },
    { value: 'year', label: 'Год' },
  ]

  const plannedAnalytics = useMemo(() => buildPlannedAnalytics(plannedTeam), [plannedTeam])
  const actualAnalytics = useMemo(() => buildActualAnalytics(actualTeam), [actualTeam])
  const actualHasRows = actualAnalytics.total > 0 || actualAnalytics.employees.length > 0 || actualAnalytics.projects.length > 0
  const plannedHasRows = plannedAnalytics.total > 0 || plannedAnalytics.stages.length > 0
  const activeTeamName = activeTeam?.name || 'Команда'
  const showUtilityControls = false
  const periodLabel = incomeQuarter === 'year'
    ? `${incomeYear}`
    : `${quarterOptions.find((option) => option.value === incomeQuarter)?.label || ''} ${incomeYear}`.trim()
  const heroNote = getDepartmentIncomeHeroNote({
    incomeQuarter,
    incomeYear,
    actualTotal,
  })
  const heroNoteIcon = heroNote?.startsWith('Эту сумму') ? 'check' : 'help'

  return (
    <div className="department-income-dashboard">
      <div className="department-detail-periodbar">
        <span className="department-detail-period-label">Период</span>
        <SegmentedControl
          ariaLabel="Квартал доходов"
          options={quarterOptions}
          value={incomeQuarter}
          onChange={setIncomeQuarter}
        />
        <span className="department-detail-period-label department-detail-period-label-year">Год</span>
        <SegmentedControl
          ariaLabel="Год доходов"
          options={yearOptions}
          value={incomeYear}
          onChange={setIncomeYear}
        />
        {showUtilityControls && (
          <>
            <FormGroup label="Сортировка" className="department-income-filter-sort">
              <Select
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
                aria-label="Сортировка команд"
              >
                <option value="name">По названию</option>
                <option value="sum_desc">По сумме ↓</option>
                <option value="sum_asc">По сумме ↑</option>
              </Select>
            </FormGroup>
            <FormGroup label="Поиск по стадии" className="department-income-filter-search">
              <Input
                type="search"
                placeholder="Название договора или стадии..."
                value={stageSearchInput}
                onChange={(event) => setStageSearchInput(event.target.value)}
                variant="search"
                iconName="search"
                aria-label="Поиск по названию стадии"
              />
            </FormGroup>
            <Button
              variant="secondary"
              onClick={handleExportCSV}
              disabled={!actualTeams.length}
              className="department-income-export-btn"
              title="Экспорт в CSV"
            >
              <Icon name="download" size={16} /> Экспорт
            </Button>
          </>
        )}
      </div>

      <section className={`department-income-hero${actualAnalytics.total <= 0 ? ' department-income-hero-empty' : ''}`}>
        <p className="department-income-hero-overline">Команда {activeTeamName} заработала за {periodLabel}</p>
        <div className="department-income-hero-amount">
          {formatMoney(actualAnalytics.total).replace(/\s?₽$/, '')}
          <span>₽</span>
        </div>
        {heroNote && (
          <div className="department-income-hero-note">
            <Icon name={heroNoteIcon} size={18} aria-hidden />
            <span>{heroNote}</span>
          </div>
        )}
      </section>

      <div className="department-income-tabs-active" role="tabpanel">
        {(actualLoading || plannedLoading) && <p className="info-note">Загрузка…</p>}
        {actualError && <p className="info-note" style={{ color: 'var(--color-error)' }}>{actualError}</p>}
        {plannedError && <p className="info-note" style={{ color: 'var(--color-error)' }}>{plannedError}</p>}

        {!actualLoading && !actualError && !actualHasRows && (
          <EmptyState
            hint={stageSearchInput.trim()
              ? 'Попробуйте изменить поисковый запрос или выбрать другой год'
              : 'Акты ещё не оформлены или выберите другой год'}
          >
            {`Нет данных о фактических доходах за ${incomeYear} год`}
          </EmptyState>
        )}

        {!actualLoading && !actualError && actualHasRows && (
          <>
            <div className="department-detail-split">
              <AmountTable
                title="По сотрудникам"
                meta={`${actualAnalytics.employees.length} ${pluralizeRu(actualAnalytics.employees.length, 'сотрудник', 'сотрудника', 'сотрудников')}`}
                columns={[
                  {
                    key: 'name',
                    label: 'Сотрудник',
                    render: (row) => (
                      <span className="department-income-entity">
                        <span className="department-income-dot" aria-hidden />
                        <span>
                          <strong>{row.name}</strong>
                          <small>{row.role}</small>
                        </span>
                      </span>
                    ),
                  },
                  { key: 'total', label: 'Заработано', align: 'right', render: (row) => formatMoney(row.total) },
                  { key: 'percent', label: '% от отдела', align: 'right', render: (row) => formatPercent(row.percent) },
                ]}
                rows={actualAnalytics.employees}
                total={actualAnalytics.employees.reduce((sum, employee) => sum + employee.total, 0)}
                formatMoney={formatMoney}
                footerCells={[
                  { value: 'Итого' },
                  {
                    value: formatMoney(actualAnalytics.employees.reduce((sum, employee) => sum + employee.total, 0)),
                    className: 'ds-mono',
                  },
                  { value: '100%', className: 'ds-mono' },
                ]}
                emptyText="Нет распределения по сотрудникам"
              />

              <ProjectIncomeTable
                projects={actualAnalytics.projects}
                total={actualAnalytics.total}
                formatMoney={formatMoney}
              />
            </div>

            <MatrixIncomeTable
              rows={actualAnalytics.matrixRows}
              total={actualAnalytics.total}
              formatMoney={formatMoney}
            />
          </>
        )}

        {!plannedLoading && !plannedError && (
          <>
            {plannedHasRows ? (
              <RemainingIncomeTable
                stages={plannedAnalytics.stages}
                formatMoney={formatMoney}
              />
            ) : (
              <EmptyState
                hint={stageSearchInput.trim()
                  ? 'Попробуйте изменить поисковый запрос или выбрать другой год'
                  : 'Добавьте стадии в договоры или выберите другой год'}
              >
                {`Нет данных об остатке по бюджетам стадий за ${incomeYear} год`}
              </EmptyState>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default DepartmentIncomeDashboard
